import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Cylinder, Package, Truck, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import { formatCurrency } from '../../components/StockIndicator';
import BetonTable from './BetonTable';
import BetonModal from './BetonModal';
import { betonSiparisler, getBetonByInsaat } from '@mocks/materials/beton';
import mockBlocks, { getBlocksByConstructionId } from '@mocks/bloklar';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Beton() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    // State — durum filtresi kaldırıldı
    const [filterDokumYeri, setFilterDokumYeri] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    // Data — tek kaynak
    const insaatId = showAllConstructions ? null : selectedConstruction?.id;
    const filteredData = useMemo(() => {
        let data = getBetonByInsaat(insaatId);
        if (filterDokumYeri) data = data.filter(item => item.dokumYeri === filterDokumYeri);
        return data;
    }, [insaatId, filterDokumYeri]);

    // Stats — filtrelenmiş veriden hesaplanıyor
    const stats = useMemo(() => {
        const toplamSiparis = filteredData.reduce((acc, s) => acc + s.miktar, 0);
        const teslimEdilen = filteredData.filter(s => s.durum === 'teslim').reduce((acc, s) => acc + s.miktar, 0);
        const bekleyen = filteredData.filter(s => s.durum === 'bekliyor').reduce((acc, s) => acc + s.miktar, 0);
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        return { toplamSiparis, teslimEdilen, bekleyen, toplamMaliyet };
    }, [filteredData]);

    // Bloklar (filtre dropdown için)
    const displayBlocks = useMemo(() => {
        if (insaatId) {
            return getBlocksByConstructionId(insaatId).map(b => ({ value: b.name, label: b.name }));
        }
        const seen = new Set();
        return mockBlocks.reduce((acc, b) => {
            if (!seen.has(b.name)) {
                seen.add(b.name);
                acc.push({ value: b.name, label: `${b.constructionName} - ${b.name}` });
            }
            return acc;
        }, []);
    }, [insaatId]);

    // Handlers
    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };

    const handleSubmit = async (formData, isEdit) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));

        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            addMockCariHareket(formData.supplierId, formData.insaatId || insaatId || 'c1', {
                type: 'purchase',
                subtype: 'fatura',
                amount: formData.kdvDahilToplam,
                date: formData.siparisTarihi || new Date().toISOString().split('T')[0],
                description: `Beton Alımı — ${formData.miktar} m³ ${formData.sinif}`,
            });
        }

        toast.success(isEdit ? 'Beton siparişi güncellendi' : 'Yeni beton siparişi oluşturuldu');
        setIsLoading(false);
        setIsModalOpen(false);
        setSelectedItem(null);

        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            setOdemePlanData({ amount: formData.kdvDahilToplam, companyName: formData.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleOdemePlanConfirm = ({ nextDueDate, paymentSubtype }) => {
        toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`);
        setIsOdemePlanOpen(false);
        setOdemePlanData(null);
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success('Beton siparişi silindi');
        setIsLoading(false);
        setIsDeleteOpen(false);
        setSelectedItem(null);
    };

    // Stats Cards
    const statsCards = [
        { title: 'Toplam Sipariş', value: `${stats.toplamSiparis} m³`, icon: Cylinder, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimEdilen} m³`, icon: Truck, color: 'success' },
        { title: 'Bekleyen', value: `${stats.bekleyen} m³`, icon: Calendar, color: 'warning' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'info' },
    ];

    // Detail Modal Fields
    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'teslimTarihi', label: 'Teslim Tarihi', type: 'date' },
        { key: 'tedarikci', label: 'Tedarikçi' },
        { key: 'sinif', label: 'Beton Sınıfı' },
        { key: 'miktar', label: 'Miktar', type: 'number' },
        { key: 'birimFiyat', label: 'Birim Fiyat', type: 'currency' },
        { key: 'toplamFiyat', label: 'Toplam Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
        { key: 'dokumYeri', label: 'Döküm Yeri', fullWidth: true },
        { key: 'sevkiyatNo', label: 'Sevkiyat No' },
    ];

    // Kategori Filtresi — header'da
    const headerFilter = (
        <select
            value={filterDokumYeri}
            onChange={(e) => setFilterDokumYeri(e.target.value)}
            className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
        >
            <option value="">Tüm Döküm Yerleri</option>
            {displayBlocks.map((b, index) => (
                <option key={index} value={b.value}>{b.label}</option>
            ))}
        </select>
    );

    return (
        <>
            <MaterialPageLayout
                title="Beton"
                subtitle="Kaba İnşaat"
                icon={Cylinder}
                iconBgColor="bg-neutral-600"
                backLink="/insaat/kaba"
                stats={statsCards}
                onAddClick={handleAdd}
                headerFilter={headerFilter}
            >
                <BetonTable
                    data={filteredData}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            </MaterialPageLayout>

            <BetonModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
                onSubmit={handleSubmit}
                initialData={selectedItem}
                isLoading={isLoading}
                insaatId={insaatId}
            />

            <MaterialDetailModal
                isOpen={isDetailOpen}
                onClose={() => { setIsDetailOpen(false); setSelectedItem(null); }}
                data={selectedItem}
                fields={detailFields}
                title="Beton Siparişi Detayı"
            />

            <ConfirmModal
                isOpen={isDeleteOpen}
                onClose={() => { setIsDeleteOpen(false); setSelectedItem(null); }}
                onConfirm={handleConfirmDelete}
                title="Siparişi Sil"
                message={`"${selectedItem?.tedarikci}" tedarikçisinden alınan ${selectedItem?.miktar} m³ beton siparişini silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil"
                isLoading={isLoading}
            />
            <OdemePlanMiniModal
                isOpen={isOdemePlanOpen}
                onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }}
                onConfirm={handleOdemePlanConfirm}
                amount={odemePlanData?.amount}
                companyName={odemePlanData?.companyName}
            />
        </>
    );
}
